import { ReactNode, useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import Button from "components/ui/button/Button";
import Popover from "components/ui/popover/Popover";
import { useContactsExportCredits } from "lib/hooks/api/contacts/useContactsExportCredits";
import { EventNames, useTracking } from "lib/tracking";
import { pluralise } from "lib/utils";
import CreditUsageBar from "./CreditUsageBar";

// Long enough to cross the 4px gap between the trigger and the panel without the panel
// flickering shut on the way.
const CLOSE_DELAY_MS = 200;

/** The credit-spending contact actions: an export, or a bulk reveal (charged the same way). */
export type ContactCreditAction = "export" | "reveal";

const ACTION_COPY: Record<
  ContactCreditAction,
  { verb: string; confirm: string; icon: "download01" | "eye" }
> = {
  export: { verb: "Exporting", confirm: "Export", icon: "download01" },
  reveal: { verb: "Revealing", confirm: "Reveal", icon: "eye" },
};

type Props = {
  /** The element that opens the popover (the Export or Reveal button). */
  trigger: ReactNode;
  /** Heading — names what's being exported or revealed (the selection, or everything matching). */
  title: string;
  /** Which action this confirms; sets the copy, and labels the credit wall event. */
  action?: ContactCreditAction;
  /** Credits the action will consume; `null` when unknown (export-all) — then it isn't credit-gated. */
  marginalCredits: number | null;
  /** Runs the action. The popover closes itself first. */
  onConfirm: () => void;
  /** Disable the confirm even when within credits (e.g. nothing to export). */
  disabled?: boolean;
};

/**
 * Confirmation panel with a live credit meter for the contact actions that spend credits. Opens
 * on hover or focus of its button — the credit cost is disclosure, not a question, so it reads
 * before you commit rather than after a click. Confirming happens on the panel's own button, which
 * is blocked (red bar + "Not enough credits") only when we know a precise cost and a known
 * balance. Export-all (unknown cost) and an unknown balance fall through to the server, which
 * re-gates authoritatively and surfaces any refusal via the action's onError toast.
 */
function ContactCreditsPopover({
  trigger,
  title,
  action = "export",
  marginalCredits,
  onConfirm,
  disabled,
}: Props) {
  const copy = ACTION_COPY[action];
  const [open, setOpen] = useState(false);
  // Whether the pointer is over the trigger or the panel — a click on the trigger of an
  // already-open panel would otherwise toggle it shut under the cursor.
  const hovering = useRef(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout>>();

  const { totalCredits, usedCredits, remainingCredits, reset, balanceKnown } =
    useContactsExportCredits();
  const { logEvent } = useTracking();
  const overLimit = balanceKnown && marginalCredits !== null && marginalCredits > remainingCredits;

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  const openNow = () => {
    hovering.current = true;
    clearTimeout(closeTimer.current);
    setOpen(true);
  };

  const closeSoon = () => {
    hovering.current = false;
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
  };

  // The credit wall: the user opened the popover and the export is blocked on balance.
  // Deduped on the shortfall itself rather than the effect's deps — `logEvent` is not
  // reference-stable (TrackingProvider rebuilds its context value on each render), so a
  // plain dep list would re-log the same wall on any ancestor re-render. Reset on close,
  // so re-opening a still-blocked export counts as a new wall.
  const loggedWall = useRef<string | null>(null);
  useEffect(() => {
    if (!open) {
      loggedWall.current = null;
      return;
    }
    if (!overLimit) return;
    const wall = `${marginalCredits}/${remainingCredits}`;
    if (loggedWall.current === wall) return;
    loggedWall.current = wall;
    logEvent(EventNames.creditLimitReached, {
      "Data type": "CONTACTS",
      Action: action,
      "Total credits": totalCredits,
      "Remaining credits": remainingCredits,
      "Required credits": marginalCredits,
    });
  }, [open, overLimit, totalCredits, remainingCredits, marginalCredits, action, logEvent]);

  // Only a mouse hovers. A tap fires `pointerenter` with no matching `pointerleave`, so
  // treating it as hover would leave the flag stuck on and swallow the tap-outside dismissal —
  // no way to close the panel on a device with no Escape key.
  const onPointerEnter = (e: React.PointerEvent) => {
    if (e.pointerType === "mouse") openNow();
  };

  const onPointerLeave = (e: React.PointerEvent) => {
    if (e.pointerType === "mouse") closeSoon();
  };

  const confirm = () => {
    if (overLimit || disabled) return;
    hovering.current = false;
    setOpen(false);
    onConfirm();
  };

  let costCopy: string;
  if (marginalCredits === null) {
    costCopy = "Credits are charged for each new contact revealed by the export.";
  } else if (marginalCredits === 0) {
    costCopy = "These contacts are already revealed, so no credits will be used.";
  } else {
    costCopy = `${copy.verb} these contacts will use ${pluralise(marginalCredits, "contact credit")}.`;
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (next) openNow();
        else if (!hovering.current) setOpen(false);
      }}
      // Escape closes even with the pointer still resting on the trigger.
      onEscapeKeyDown={() => {
        hovering.current = false;
        setOpen(false);
      }}
      // Hovering must not pull focus out of whatever the user was doing.
      onOpenAutoFocus={(e) => e.preventDefault()}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      // Focus entering the panel has to hold it open: tabbing off the trigger blurs it, and
      // without this the pending close fires while the confirm button has focus, taking the
      // panel out from under the keyboard.
      onFocus={openNow}
      onBlur={closeSoon}
      hideArrow
      align="end"
      contentPadding={0}
      borderRadius={8}
      trigger={
        <TriggerWrapper
          onPointerEnter={onPointerEnter}
          onPointerLeave={onPointerLeave}
          onFocus={openNow}
          onBlur={closeSoon}
        >
          {trigger}
        </TriggerWrapper>
      }
    >
      <Card>
        <Section gap={8}>
          <Text fontWeight="600">{title}</Text>
          <Text>{costCopy}</Text>
        </Section>
        <Section gap={16}>
          <CreditUsageBar
            totalCredits={totalCredits}
            usedCredits={usedCredits}
            remainingCredits={remainingCredits}
            marginalCredits={marginalCredits ?? 0}
            resetDate={reset?.date}
          />
          <Button
            buttonType="primary"
            icon={overLimit ? undefined : copy.icon}
            fullWidth
            disabled={overLimit || disabled}
            onClick={confirm}
          >
            {overLimit ? "Not enough credits" : copy.confirm}
          </Button>
        </Section>
      </Card>
    </Popover>
  );
}

// Carries the hover handlers for the button it wraps: Radix hands the trigger its own props,
// so the pointer tracking sits on a wrapper rather than fighting them.
const TriggerWrapper = styled.span({ display: "inline-flex" });

const Card = styled.div(({ theme }) => ({
  width: 320,
  textAlign: "left",
  borderRadius: 8,
  overflow: "hidden",
  border: `1px solid ${theme.colors.sysBorderDecorative}`,
}));

// Each block gets its own padded, separated section — the design divides the explanation from
// the meter and its confirm.
const Section = styled(Flex)(({ theme }) => ({
  flexDirection: "column",
  padding: 16,
  "&:not(:last-of-type)": { borderBottom: `1px solid ${theme.colors.sysBorderDecorative}` },
}));

export default ContactCreditsPopover;
